import { v4 } from 'uuid';
import { z } from 'zod';
import * as FlatModels from "../../internal";

export const ErrorLevel = z.enum([
  'critical',
  'error',
  'system',
  'debug',
  'warning',
  'notice',
  'info',
]);
export type ErrorLevel = z.infer<typeof ErrorLevel>;

export const ErrorLevelAlertTypeSchema = z.record(ErrorLevel, FlatModels.AlertControlModels.AlertType);

export const ErrorLevelAlertType: z.infer<typeof ErrorLevelAlertTypeSchema> = {
  [ErrorLevel.enum.critical]: FlatModels.AlertControlModels.AlertType.enum.error,
  [ErrorLevel.enum.error]: FlatModels.AlertControlModels.AlertType.enum.error,
  [ErrorLevel.enum.system]: FlatModels.AlertControlModels.AlertType.enum.error,
  [ErrorLevel.enum.debug]: FlatModels.AlertControlModels.AlertType.enum.warn,
  [ErrorLevel.enum.warning]: FlatModels.AlertControlModels.AlertType.enum.warn,
  [ErrorLevel.enum.notice]: FlatModels.AlertControlModels.AlertType.enum.info,
  [ErrorLevel.enum.info]: FlatModels.AlertControlModels.AlertType.enum.info,
}

export const ErrorInstance = z.enum([
  'API',
  'AUTHOR',
  'VIEWER',
]);
export type ErrorInstance = z.infer<typeof ErrorInstance>;

export const ErrorOptionsSchema = z.object({
  show: z.boolean().optional(),
  logout: z.boolean().optional(),
  redirectToLogin: z.boolean().optional(),
  allowedRedirectUrl: z.string().url().default('login').optional(),
  redirectToErrorPage: z.boolean().optional(),
  httpCode: z.number().optional(),
  // TODO toSentry: z.boolean().optional()
  // TODO change args from unknown[] to object so we can log it better
});

export type ErrorOptions = z.infer<typeof ErrorOptionsSchema>;

export const InputErrorOptionsSchema = ErrorOptionsSchema.extend({
  data: z.any().optional(),
  fieldErrors: z.record(z.string(), z.array(z.string())).optional()
});

export type InputErrorOptions = z.infer<typeof InputErrorOptionsSchema>;

export const MedsurfErrorEntityTypes = z.enum([
  'MedsurfError',
  'MedsurfUnhandledError',
  'MedsurfUnauthorizedError',
  'MedsurfInputError',
  'MedsurfNotFoundError',
]);
export type MedsurfErrorEntityTypes = z.infer<typeof MedsurfErrorEntityTypes>;

export class MedsurfError extends Error {
  options: ErrorOptions;
  timestamp: number;
  uuid: string;

  constructor(message: string,
              public level: ErrorLevel,
              public instance: ErrorInstance,
              public errorCode: string,
              options: ErrorOptions = {},
              public args: unknown[] = []) {
    const defaultOptions: ErrorOptions = {
      show: true,
      logout: false,
      redirectToLogin: false,
      redirectToErrorPage: false,
      httpCode: 400,
    };
    super(message);
    this.name = MedsurfErrorEntityTypes.enum.MedsurfError;
    this.options = Object.assign(defaultOptions, options);
    this.timestamp = Date.now();
    this.uuid = v4();
  }
  
  logError() {
    console.error(this);
  }

  override toString() {
    // "stack": "${this.stack}",
    return `{
      "message":"${this.message}",
      "level":"${this.level}",
      "instance":"${this.instance}",
      "errorCode":"${this.errorCode}",
      "options":{
        "show":${this.options.show},
        "logout":${this.options.logout},
        "redirectToLogin":${this.options.redirectToLogin},
        "allowedRedirectUrl":${this.options.allowedRedirectUrl},
        "redirectToErrorPage":${this.options.redirectToErrorPage},
        "httpCode":"${this.options.httpCode}"
      },
      "args":${JSON.stringify(this.args)},
      "name":"${this.name}",
      "timestamp":${this.timestamp},
      "uuid":"${this.uuid}"
    }`.replace(/(?:\r\n|\r|\n)/g, '');
  }
  
  getExtras() {
    return {
      'Message': this.message,
      'Level': this.level,
      'ErrorCode': this.errorCode,
      'Options': this.options,
      'Args': JSON.stringify(this.args),
      'Name': this.name,
    }
  }
}

export class MedsurfUnhandledError extends MedsurfError {
  constructor(message: string,
              errorCode: string,
              options: ErrorOptions = {},
              args: unknown[] = []) {
    const defaultOptions: ErrorOptions = {
      httpCode: 401
    };
    super(message,
      ErrorLevel.enum.error,
      ErrorInstance.enum.API,
      errorCode,
      Object.assign(defaultOptions, options),
      args
    );
    this.name = MedsurfErrorEntityTypes.enum.MedsurfUnhandledError;
  }

  override toString() {
    // "stack": "${this.stack}",
    return `{
      "message":"notification.unknown_api_error",
      "level":"${this.level}",
      "instance":"${this.instance}",
      "errorCode":"${this.errorCode}",
      "options":{
        "show":${this.options.show},
        "logout":${this.options.logout},
        "redirectToLogin":${this.options.redirectToLogin},
        "allowedRedirectUrl":${this.options.allowedRedirectUrl},
        "redirectToErrorPage":${this.options.redirectToErrorPage},
        "httpCode":"${this.options.httpCode}"
      },
      "args":${JSON.stringify(this.args)},
      "name":"${this.name}",
      "timestamp":${this.timestamp},
      "uuid":"${this.uuid}"
    }`.replace(/(?:\r\n|\r|\n)/g, '');
  }
  
  override getExtras() {
    return {
      'Message': this.message,
      'Level': this.level,
      'ErrorCode': this.errorCode,
      'Options': this.options,
      'Args': JSON.stringify(this.args),
      'Name': this.name,
    }
  }
}

export class MedsurfUnauthorizedError extends MedsurfError {
  constructor(message: string,
              errorCode: string,
              options: ErrorOptions = {},
              args: unknown[] = []) {
    const defaultOptions: ErrorOptions = {
      httpCode: 401,
      redirectToLogin: true,
      allowedRedirectUrl: '/login'
    };
    super(
      message,
      ErrorLevel.enum.error,
      ErrorInstance.enum.API,
      errorCode,
      Object.assign(defaultOptions, options),
      args
    );
    this.name = MedsurfErrorEntityTypes.enum.MedsurfUnauthorizedError;
  }
}

export class MedsurfInputError extends MedsurfError {
  override options!: InputErrorOptions;

  constructor(message: string,
              errorCode: string,
              options: InputErrorOptions,
              args: unknown[] = []) {
    super(
      message,
      ErrorLevel.enum.info,
      ErrorInstance.enum.API,
      errorCode,
      options,
      args
    );
    this.name = MedsurfErrorEntityTypes.enum.MedsurfInputError;
  }
  
  override logError() {
    console.error(this);
    console.error("data", JSON.stringify(this.options.data));
    console.error("fieldErrors", JSON.stringify(this.options.fieldErrors));
  }

  override toString() {
    // "stack": "${this.stack}",
    return `{
      "message":"${this.message}",
      "level":"${this.level}",
      "instance":"${this.instance}",
      "errorCode":"${this.errorCode}",
      "options":{
        "show":${this.options.show},
        "logout":${this.options.logout},
        "redirectToLogin":${this.options.redirectToLogin},
        "allowedRedirectUrl":${this.options.allowedRedirectUrl},
        "redirectToErrorPage":${this.options.redirectToErrorPage},
        "httpCode":"${this.options.httpCode}",
        "fieldErrors":${JSON.stringify(this.options.fieldErrors)}
      },
      "args":${JSON.stringify(this.args)},
      "name":"${this.name}",
      "timestamp":${this.timestamp}
      "uuid":"${this.uuid}"
    }`.replace(/(?:\r\n|\r|\n)/g, '');
  }
  
  override getExtras() {
    return {
      'Message': this.message,
      'Level': this.level,
      'ErrorCode': this.errorCode,
      'Options': this.options,
      'Args': JSON.stringify(this.args),
      'Name': this.name,
    }
  }
}

export class MedsurfNotFoundError extends MedsurfError {
  constructor(message: string,
              errorCode: string,
              options: ErrorOptions = {},
              args: unknown[] = []) {
    const defaultOptions: ErrorOptions = {
      httpCode: 404,
      redirectToErrorPage: true
    };
    super(
      message,
      ErrorLevel.enum.error,
      ErrorInstance.enum.API,
      errorCode,
      Object.assign(defaultOptions, options),
      args
    );
    this.name = MedsurfErrorEntityTypes.enum.MedsurfNotFoundError;
  }
}

export const MedsurfErrorTypeSchema = z.union([
  z.instanceof(MedsurfError),
  z.instanceof(MedsurfUnhandledError),
  z.instanceof(MedsurfUnauthorizedError),
  z.instanceof(MedsurfInputError),
  z.instanceof(MedsurfNotFoundError)
]);

export type MedsurfErrorType = z.infer<typeof MedsurfErrorTypeSchema>;

export const MedsurfErrorNames = [
  'MedsurfError',
  'MedsurfUnhandledError',
  'MedsurfUnauthorizedError',
  'MedsurfInputError',
  'MedsurfNotFoundError'
];
